import {
  EXCLUDE_ALL_OF,
  EXCLUDE_ANY_OF,
  INCLUDE_ALL_OF,
  INCLUDE_ANY_OF,
  type MultiSelectFilterModifier,
} from '@iconik-io/shared-lib/src/redux/common_filters/types';

export type ModifierOption = {
  value: MultiSelectFilterModifier;
  label: string;
};

const allModifierOptions: ModifierOption[] = [
  { value: INCLUDE_ALL_OF, label: 'include all of' },
  { value: INCLUDE_ANY_OF, label: 'include any of' },
  { value: EXCLUDE_ALL_OF, label: 'exclude all of' },
  { value: EXCLUDE_ANY_OF, label: 'exclude any of' },
];

/**
 * Display text for a modifier. A single selected item collapses every modifier
 * to a plain "include"/"exclude" (the `_all_of`/`_any_of` distinction is only
 * meaningful with multiple items).
 */
export function getModifierDisplayLabel(
  modifier: MultiSelectFilterModifier,
  itemCount: number,
): string {
  if (itemCount <= 1) {
    return modifier === EXCLUDE_ALL_OF || modifier === EXCLUDE_ANY_OF
      ? 'exclude'
      : 'include';
  }
  switch (modifier) {
    case INCLUDE_ALL_OF:
      return 'include all of';
    case INCLUDE_ANY_OF:
      return 'include any of';
    case EXCLUDE_ALL_OF:
      return 'exclude all of';
    case EXCLUDE_ANY_OF:
      return 'exclude any of';
  }
}

/**
 * The modifier options offered for a filter, matching the filter panel's
 * modifier dropdown. When only one item is selected the list collapses to a
 * single "include"/"exclude" pair (no duplicated labels), preferring the
 * `_all_of` variants and falling back to `_any_of` for fields that only allow
 * `_any_of` modifiers.
 */
export function getAvailableModifierOptions(
  availableModifiers: MultiSelectFilterModifier[],
  itemCount: number,
): ModifierOption[] {
  if (itemCount <= 1) {
    const includeValue = ([INCLUDE_ALL_OF, INCLUDE_ANY_OF] as const).find(
      (modifier) => availableModifiers.includes(modifier),
    );
    const excludeValue = ([EXCLUDE_ALL_OF, EXCLUDE_ANY_OF] as const).find(
      (modifier) => availableModifiers.includes(modifier),
    );
    const singleOptions: ModifierOption[] = [];
    if (includeValue) {
      singleOptions.push({ value: includeValue, label: 'include' });
    }
    if (excludeValue) {
      singleOptions.push({ value: excludeValue, label: 'exclude' });
    }
    return singleOptions;
  }
  return allModifierOptions.filter((option) =>
    availableModifiers.includes(option.value),
  );
}

/**
 * Maps a stored modifier onto a value that {@link getAvailableModifierOptions}
 * actually offers, so the current selection stays checked and its label agrees
 * with the option. A mismatch arises from the single-item collapse (which
 * prefers `*_all_of`) or from a field that restricts its modifier set (the
 * storage collections clamp, a single-select drop_down): in both cases it falls
 * back to the same include/exclude family's offered variant.
 */
export function normalizeModifier(
  modifier: MultiSelectFilterModifier,
  availableModifiers: MultiSelectFilterModifier[],
  itemCount: number,
): MultiSelectFilterModifier {
  const isExclude = modifier === EXCLUDE_ALL_OF || modifier === EXCLUDE_ANY_OF;
  const allOf = isExclude ? EXCLUDE_ALL_OF : INCLUDE_ALL_OF;
  const anyOf = isExclude ? EXCLUDE_ANY_OF : INCLUDE_ANY_OF;

  if (itemCount <= 1) {
    // The single collapsed option for this family prefers *_all_of.
    if (availableModifiers.includes(allOf)) {
      return allOf;
    }
    if (availableModifiers.includes(anyOf)) {
      return anyOf;
    }
    return modifier;
  }
  if (availableModifiers.includes(modifier)) {
    return modifier;
  }
  const counterpart = modifier === allOf ? anyOf : allOf;
  return availableModifiers.includes(counterpart) ? counterpart : modifier;
}
